import { getFormProps, useForm } from '@conform-to/react';
import { getZodConstraint, parseWithZod } from '@conform-to/zod';
import { Form } from 'react-router';
import { AuthenticityTokenInput } from 'remix-utils/csrf/react';
import { z } from 'zod';

import { SelectField, SelectItem } from '~/components/forms/select-field';
import { SubmitButton } from '~/components/forms/submit-button';
import { TextField } from '~/components/forms/text-field';
import {
  NULLABLE_STRING_TO_NUMBER,
  PHONE_REGEX,
  SHORT_STRING,
  nullableField,
} from '~/utils/validation';

interface State {
  id: number;
  name: string;
  abbreviation: string;
}

export const schema = z.object({
  firstName: SHORT_STRING,
  lastName: SHORT_STRING,
  email: SHORT_STRING.email(),
  phoneNumber: z.string().regex(PHONE_REGEX, 'Must be a valid phone number'),
  city: nullableField(SHORT_STRING),
  stateId: NULLABLE_STRING_TO_NUMBER,
  gymName: nullableField(SHORT_STRING),
  gymUrl: nullableField(SHORT_STRING.url()),
});

export default function RequestDemoForm({ states }: { states: State[] }) {
  const [form, fields] = useForm({
    shouldValidate: 'onBlur',
    constraint: getZodConstraint(schema),
    onValidate: ({ formData }) => parseWithZod(formData, { schema }),
  });

  return (
    <div className="rounded-2xl bg-gray-200 p-12">
      <h2 className="mb-6 text-h5">Request a Free Demo</h2>
      <Form {...getFormProps(form)} method="POST">
        <AuthenticityTokenInput />

        <p className="mb-6 text-caption font-normal">*Required Field</p>

        <div className="grid grid-cols-6 gap-x-6">
          <TextField
            field={fields.firstName}
            label="First Name"
            withAsterisk
            inputProps={{ autoComplete: 'given-name', className: 'bg-transparent' }}
            className="col-span-3 bg-none"
          />

          <TextField
            field={fields.lastName}
            label="Last Name"
            withAsterisk
            inputProps={{ autoComplete: 'family-name', className: 'bg-transparent' }}
            className="col-span-3"
          />

          <TextField
            field={fields.email}
            label="Email"
            withAsterisk
            inputProps={{ type: 'email', className: 'bg-transparent' }}
            className="col-span-full"
          />

          <TextField
            field={fields.phoneNumber}
            label="Phone Number"
            withAsterisk
            inputProps={{
              type: 'tel',
              autoComplete: 'tel',
              className: 'bg-transparent',
            }}
            className="col-span-full"
          />

          <TextField
            field={fields.city}
            label="City"
            inputProps={{ autoComplete: 'address-level2', className: 'bg-transparent' }}
            className="col-span-4"
          />

          <SelectField
            field={fields.stateId}
            label="State"
            placeholder="Select…"
            selectProps={{ autoComplete: 'address-level1', className: 'bg-transparent' }}
            className="col-span-2"
          >
            {states.map((s) => (
              <SelectItem key={s.id} value={s.id.toString()}>
                {s.name}
              </SelectItem>
            ))}
          </SelectField>

          <TextField
            field={fields.gymName}
            label="Gym Name"
            className="col-span-full"
            inputProps={{ className: 'bg-transparent' }}
          />

          <TextField
            field={fields.gymUrl}
            label="Gym URL"
            inputProps={{ type: 'url', autoComplete: 'url', className: 'bg-transparent' }}
            className="col-span-full"
          />
        </div>

        <div>
          <SubmitButton className="ml-auto">Submit</SubmitButton>
        </div>
      </Form>
    </div>
  );
}
